
import React, { useEffect, useState } from 'react';
import { complianceService } from '../../services/api';
import Navbar from '../Dashboard/components/Navbar';
import Footer from '../Dashboard/components/Footer';
import PropertyCard from './components/PropertyCard';
import PropertyDetail from './PropertyDetail';
import PRSReadiness from './components/PRSReadiness';
import PRSWizard from './components/PRSWizard';
import { computeDocSlots, findDocCert } from './components/TexasPropertySections';
import { useNavigate } from 'react-router-dom';
import { calculateComplianceScore } from '../../utils/complianceCalculator';
import { clearSessionPreservingPreferences } from '../../utils/sessionStorage';
import { isHousingPro } from '../../utils/featureFlags';
import { useSystemFlag } from '../../config/useSystemFlags';
import { getAppMarket } from '../../services/MarketService';
import '../HousingPro/HousingPro.css';

// Error Boundary for PropertyDetail
class PropertyDetailErrorBoundary extends React.Component<
    { children: React.ReactNode; onClose: () => void },
    { hasError: boolean }
> {
    constructor(props: any) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(err: any) {
        console.error('[PropertyDetailErrorBoundary] Critical Crash:', err);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="flex-1 flex flex-col items-center justify-center p-8 bg-white h-full overflow-y-auto">
                    <div className="text-center max-w-sm">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-rose-50 rounded-full mb-6 border border-rose-100 shadow-sm">
                            <i className="fas fa-exclamation-triangle text-rose-500 text-2xl"></i>
                        </div>
                        <h3 className="text-lg font-black text-slate-900 uppercase tracking-tighter mb-2">Operational Failure</h3>
                        <p className="text-xs text-slate-500 font-medium mb-8 leading-relaxed">
                            The system encountered a critical fault while rendering this property asset. The error has been captured for remediation.
                        </p>
                        <button 
                            onClick={this.props.onClose}
                            className="w-full py-3 bg-slate-900 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-emerald-600 transition-all shadow-lg"
                        >
                            Return to Portfolio
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default function Properties() {
    const navigate = useNavigate();
    const prsReadinessVisible = useSystemFlag('prsDatabaseReadiness');
    const prsWizardVisible = useSystemFlag('prsSubmissionWizard');
    // F-307: PRS is UK terminology — the Texas market brands the same flow
    // as Texas Registration (human directive: "Texas registration throughout").
    const isTexasMarket = getAppMarket() === 'texas';
    const [properties, setProperties] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(50);
    const [hasMore, setHasMore] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    // Filter — supports a ?filter=... query param so the dashboard cards
    // can deep-link straight to a filtered view without an extra click.
    // Supported values:
    //   vacant             — any property with spare capacity (resi 0-tenant + HMO 0-tenant + HMO partial)
    //   vacant_residential — single-let residential with no tenants
    //   vacant_hmo         — HMO with zero tenants
    //   hmo_partial        — HMO with at least one tenant but not at capacity
    //   compliant / at_risk — compliance based
    const initialFilter = (() => {
        try {
            const p = new URLSearchParams(window.location.search).get('filter');
            if (p === 'vacant')             return 'VACANT';
            if (p === 'vacant_residential') return 'VACANT_RESIDENTIAL';
            if (p === 'vacant_hmo')         return 'VACANT_HMO';
            if (p === 'hmo_partial')        return 'HMO_PARTIAL';
            if (p === 'compliant')          return 'COMPLIANT';
            if (p === 'at_risk')            return 'AT_RISK';
        } catch { }
        return 'ALL';
    })();
    const [filterStatus, setFilterStatus] = useState<'ALL' | 'COMPLIANT' | 'AT_RISK' | 'VACANT' | 'VACANT_RESIDENTIAL' | 'VACANT_HMO' | 'HMO_PARTIAL'>(initialFilter as any);
    const [groupBy, setGroupBy] = useState<'NONE' | 'CITY' | 'POSTCODE'>('NONE');
    const [sortBy, setSortBy] = useState<'ADDRESS' | 'SCORE'>('ADDRESS');
    const [loadingScores, setLoadingScores] = useState<Set<string>>(new Set());
    const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(null);
    const [showWizard, setShowWizard] = useState(false);

    useEffect(() => {
        const fetchProperties = async () => {
            try {
                const response = await complianceService.getProperties();
                let propertiesData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
                const now = new Date();
                const texasNow = getAppMarket() === 'texas';
                const propertiesWithDefaults = propertiesData.map((prop: any) => {
                    const certs: any[] = prop.certificates || [];
                    // F-308: Texas is scored against the US document vault (the
                    // same slots TexasDocumentValidation renders), never the UK
                    // Gas Safety / EPC / EICR trio.
                    const criticalDefs = texasNow
                        ? null
                        : [
                            { key: 'gas',  aliases: ['gas_safety', 'gas', 'cp12', 'gassafety'] },
                            { key: 'epc',  aliases: ['epc', 'energy'] },
                            { key: 'eicr', aliases: ['eicr', 'electrical'] },
                          ];
                    // Denominator: how many critical documents this market needs.
                    let denominator = 3;
                    if (texasNow) {
                        const requiredSlots = computeDocSlots(
                            prop.yearBuilt ?? prop.builtYear ?? prop.year_built
                        ).filter((s) => s.required);
                        denominator = Math.max(requiredSlots.length, 1);
                    }
                    let presentCount = 0;   // critical types uploaded at all
                    let validCount = 0;     // critical types uploaded AND not expired
                    let expiredCount = 0;
                    if (texasNow) {
                        computeDocSlots(
                            prop.yearBuilt ?? prop.builtYear ?? prop.year_built
                        ).filter((s) => s.required).forEach((slot) => {
                            const found = findDocCert(certs, slot);
                            if (!found) return;
                            presentCount++;
                            const expiry = found.expires || found.expiryDate;
                            const isExpired = expiry && new Date(expiry) < now;
                            if (isExpired) expiredCount++;
                            else validCount++;
                        });
                    } else {
                        criticalDefs!.forEach(def => {
                            const found = certs.find((c: any) => {
                                const t = (c.type || '').toLowerCase().replace(/[\s_-]/g, '');
                                return def.aliases.some(a => t.includes(a.replace(/_/g, '')));
                            });
                            if (found) {
                                presentCount++;
                                const expiry = found.expires || found.expiryDate;
                                const isExpired = expiry && new Date(expiry) < now;
                                if (isExpired) expiredCount++;
                                else validCount++;
                            }
                        });
                    }
                    // Compliant (100%) ONLY when every critical type is present
                    // AND all of them are still valid (not expired).
                    // Otherwise score reflects how many are present-and-valid.
                    const allPresentAndValid = validCount >= denominator;
                    const complianceScore = prop.hasAllCriticalDocs === true || allPresentAndValid
                        ? 100
                        : certs.length > 0 ? Math.round((validCount / denominator) * 100)
                        : null;
                    return { ...prop, complianceScore, criticalCount: presentCount, expiredCount };
                });
                setProperties(propertiesWithDefaults);
                setHasMore(propertiesWithDefaults.length > pageSize);
            } catch (error: any) {
                console.error("Failed to fetch properties", error);
                if (error?.response?.status === 401) {
                    clearSessionPreservingPreferences();
                    window.location.href = '/login';
                }
            } finally {
                setLoading(false);
            }
        };
        fetchProperties();
    }, []);

    useEffect(() => {
        if (properties.length === 0) return;
        // TODO: Re-enable with pagination/lazy loading
        // properties.forEach((prop, index) => {
        //     if (prop.complianceScore === null) {
        //         setTimeout(() => {
        //             loadComplianceScore(prop.id);
        //         }, index * 200);
        //     }
        // });
    }, [properties.length]);

    const loadComplianceScore = async (propertyId: string) => {
        if (loadingScores.has(propertyId)) return;
        const property = properties.find(p => p.id === propertyId);
        if (!property || property.complianceScore !== null) return;
        setLoadingScores(prev => new Set(prev).add(propertyId));
        try {
            const response = await complianceService.getProperty(propertyId);
            const propData = response.data.data || response.data;
            const certificates = propData.certificates || [];
            const hasGas = propData.hasGasInstallation !== false;
            const score = calculateComplianceScore(certificates, hasGas);
            setProperties(prev => prev.map(p =>
                p.id === propertyId ? { ...p, complianceScore: score } : p
            ));
        } catch (error) {
            console.error(`Failed to load score for ${propertyId}:`, error);
            setProperties(prev => prev.map(p =>
                p.id === propertyId ? { ...p, complianceScore: 0 } : p
            ));
        } finally {
            setLoadingScores(prev => {
                const next = new Set(prev);
                next.delete(propertyId);
                return next;
            });
        }
    };

    const filteredProperties = properties
        .filter(prop => {
            // Search across every field a user might type. Property addresses in
            // this codebase land under either the top-level string, an object
            // with line1/line2/street/city/postcode, or the legacy top-level
            // `postcode`. Concatenate everything we can find and match once.
            const addr = (typeof prop.address === 'object' ? prop.address : null) as any;
            const haystack = [
                typeof prop.address === 'string' ? prop.address : '',
                addr?.line1, addr?.line2, addr?.street, addr?.city, addr?.county, addr?.postcode,
                prop.postcode,
            ].filter(Boolean).join(' ').toLowerCase();
            const needle = searchTerm.trim().toLowerCase();
            const matchesSearch = !needle || haystack.includes(needle);

            // COMPLIANT = has all 3 critical docs (score = 100) OR explicitly flagged
            // 50/50 = score >= 50 but < 100 (has some docs)
            // AT_RISK = score < 50 or no data
            const score = prop.complianceScore;
            const isCompliant = score === 100 || prop.hasAllCriticalDocs === true;
            const isAtRisk = !isCompliant;
            // Occupancy classification — uses tenantStatus set by the
            // /api/properties enrichment: 'Vacant' (no tenants), 'Partial'
            // (HMO with some rooms let), 'Occupied' (fully let).
            const ts = (prop.tenantStatus || '').toLowerCase();
            const isHMO = (prop.type || '').toLowerCase() === 'hmo';
            const isFullyVacant      = ts === 'vacant';
            const isPartiallyOccupied = ts === 'partial';
            const isVacantOrPartial   = isFullyVacant || isPartiallyOccupied;
            const isVacantResidential = isFullyVacant && !isHMO;
            const isVacantHMO         = isFullyVacant && isHMO;
            const isHmoPartial        = isPartiallyOccupied && isHMO;

            const matchesFilter = filterStatus === 'ALL' ||
                (filterStatus === 'COMPLIANT'           && isCompliant) ||
                (filterStatus === 'AT_RISK'             && isAtRisk) ||
                (filterStatus === 'VACANT'              && isVacantOrPartial) ||
                (filterStatus === 'VACANT_RESIDENTIAL' && isVacantResidential) ||
                (filterStatus === 'VACANT_HMO'         && isVacantHMO) ||
                (filterStatus === 'HMO_PARTIAL'        && isHmoPartial);

            return matchesSearch && matchesFilter;
        })
        .sort((a, b) => {
            if (sortBy === 'SCORE') return (b.complianceScore || 0) - (a.complianceScore || 0);
            const addrA = (a.address?.line1 || '').toLowerCase();
            const addrB = (b.address?.line1 || '').toLowerCase();
            return addrA.localeCompare(addrB);
        });

    const groupedProperties = React.useMemo(() => {
        if (groupBy === 'NONE') return { 'All Properties': filteredProperties };
        
        const groups: Record<string, any[]> = {};
        filteredProperties.forEach(prop => {
            let key = 'Other';
            if (groupBy === 'CITY') key = prop.address?.city || 'Unspecified City';
            else if (groupBy === 'POSTCODE') key = prop.address?.postcode?.split(' ')[0] || 'Unspecified Area';
            
            if (!groups[key]) groups[key] = [];
            groups[key].push(prop);
        });
        return groups;
    }, [filteredProperties, groupBy]);


    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col">
                <Navbar />
                <div className="flex-1 flex justify-center items-center">
                    <div className="animate-pulse flex flex-col items-center">
                        <div className="h-12 w-12 bg-green-200 rounded-full mb-4"></div>
                        <div className="h-4 w-32 bg-gray-200 rounded"></div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gray-50 min-h-screen font-sans flex flex-col">
            <Navbar />

            <div className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">

                {/* Header Section */}
                <div className="md:flex md:items-center md:justify-between mb-8">
                    <div className="flex-1 min-w-0">
                        <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                            My Properties
                        </h2>
                        <p className="mt-1 text-sm text-gray-500">
                            Manage and monitor compliance across your {properties.length} properties.
                        </p>
                    </div>
                    <div className="mt-4 flex md:mt-0 md:ml-4 space-x-3">
                        <button
                            type="button"
                            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                            onClick={() => navigate('/properties/add')}
                        >
                            <i className="fas fa-plus mr-2"></i>
                            Add Property
                        </button>
                        {(isTexasMarket || prsWizardVisible) && (
                            <button
                                type="button"
                                className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                onClick={() => setShowWizard(true)}
                            >
                                <i className={`fas ${isTexasMarket ? 'fa-clipboard-check' : 'fa-wand-magic'} mr-2`}></i>
                                {isTexasMarket ? 'Texas Registration' : 'PRS Submission Wizard'}
                            </button>
                        )}
                    </div>
                </div>

                {/* PRS Database readiness — hidden globally when SA disables the
                    prsDatabaseReadiness flag, and always hidden for non-enterprise
                    (isHousingPro) tenants regardless. Either can suppress. */}
                {!isTexasMarket && isHousingPro() && prsReadinessVisible && (
                    <div className="mb-6">
                        <PRSReadiness />
                    </div>
                )}

                {/* Filter Bar */}
                <div className="bg-white rounded-xl border border-gray-200 p-3 mb-6 shadow-sm flex flex-col lg:flex-row gap-3 items-center">
                    <div className="relative flex-1 w-full">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <i className="fas fa-search text-slate-300 text-xs"></i>
                        </div>
                        <input
                            type="text"
                            className="block w-full pl-9 pr-3 py-1.5 border border-slate-200 rounded-lg text-sm placeholder-slate-400 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                            placeholder="Find property..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto">
                        <div className="flex items-center gap-1.1 bg-slate-50 p-1 rounded-lg border border-slate-100">
                            <span className="text-[10px] font-black text-slate-400 px-2 uppercase">Status</span>
                            <select
                                className="bg-transparent text-xs font-bold text-slate-700 focus:outline-none pr-4"
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value as any)}
                            >
                                <option value="ALL">All Properties</option>
                                <option value="COMPLIANT">Compliant</option>
                                <option value="AT_RISK">Action Required</option>
                                <option value="VACANT">Vacant or Partial (any spare capacity)</option>
                                <option value="VACANT_RESIDENTIAL">Residential — Vacant</option>
                                {!isTexasMarket && <option value="VACANT_HMO">HMO — Fully Vacant</option>}
                                {!isTexasMarket && <option value="HMO_PARTIAL">HMO — Partially Occupied</option>}
                            </select>
                        </div>

                        <div className="flex items-center gap-1.5 bg-slate-50 p-1 rounded-lg border border-slate-100">
                            <span className="text-[10px] font-black text-slate-400 px-2 uppercase">Group</span>
                            <select
                                className="bg-transparent text-xs font-bold text-slate-700 focus:outline-none pr-4"
                                value={groupBy}
                                onChange={(e) => setGroupBy(e.target.value as any)}
                            >
                                <option value="NONE">None</option>
                                <option value="CITY">By City</option>
                                <option value="POSTCODE">{isTexasMarket ? 'By ZIP Code' : 'By Postcode'}</option>
                            </select>
                        </div>

                        <div className="flex items-center gap-1.5 bg-slate-50 p-1 rounded-lg border border-slate-100">
                            <span className="text-[10px] font-black text-slate-400 px-2 uppercase">Sort</span>
                            <select
                                className="bg-transparent text-xs font-bold text-slate-700 focus:outline-none pr-4"
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value as any)}
                            >
                                <option value="ADDRESS">Address</option>
                                <option value="SCORE">Score</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Properties Grid */}
                {filteredProperties.length > 0 ? (
                    <div className="space-y-8">
                        {Object.entries(groupedProperties).map(([groupName, groupItems]) => {
                            // Paginate items - slice based on current page
                            const paginatedItems = groupItems.slice(0, page * pageSize);
                            return (
                            <div key={groupName}>
                                {groupBy !== 'NONE' && (
                                    <div className="flex items-center gap-3 mb-4">
                                        <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest">{groupName}</h3>
                                        <div className="h-[1px] flex-1 bg-slate-100"></div>
                                        <span className="text-[10px] font-black text-slate-300 bg-slate-50 px-2 py-0.5 rounded-full border border-slate-100">
                                            {paginatedItems.length} / {groupItems.length} UNITS
                                        </span>
                                    </div>
                                )}
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                                    {paginatedItems.map((prop, idx) => (
                                        <PropertyCard
                                            key={prop.id || idx}
                                            property={prop}
                                            onClick={(id) => setSelectedPropertyId(id)}
                                        />
                                    ))}
                                </div>
                            </div>
                            );
                        })}

                        {/* Load More Button */}
                        {hasMore && filteredProperties.length > page * pageSize && (
                            <div className="flex justify-center mt-8">
                                <button
                                    onClick={() => setPage(page + 1)}
                                    disabled={loadingMore}
                                    className="px-8 py-3 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-all"
                                >
                                    {loadingMore ? 'Loading...' : `Load More (${Math.min(filteredProperties.length - page * pageSize, pageSize)} more)`}
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    /* Empty State */
                    <div className="text-center py-24 bg-white rounded-2xl border border-dashed border-slate-200">
                        <div className="mx-auto h-20 w-20 bg-slate-50 rounded-full flex items-center justify-center mb-6 border border-slate-100 shadow-sm">
                            <i className="fas fa-home text-3xl text-slate-300"></i>
                        </div>
                        <h3 className="text-base font-black text-slate-800 uppercase tracking-tight">No properties found</h3>
                        <p className="text-xs text-slate-400 font-medium mt-1 max-w-xs mx-auto">
                            {properties.length === 0
                                ? "Your portfolio is currently empty. Start by adding your first property."
                                : "Adjust your search or filters to find what you're looking for."}
                        </p>
                        {properties.length === 0 && (
                            <div className="mt-8">
                                <button
                                    type="button"
                                    className="inline-flex items-center px-6 py-2 border border-transparent text-xs font-black uppercase tracking-widest rounded-lg text-white bg-slate-900 hover:bg-emerald-600 focus:outline-none transition-all shadow-lg"
                                    onClick={() => navigate('/properties/add')}
                                >
                                    <i className="fas fa-plus mr-2"></i>
                                    Connect Asset
                                </button>
                            </div>
                        )}
                    </div>
                )}


            </div>

            {/* Property Details Drawer Modal */}
            {selectedPropertyId && (
                <div
                    className="fixed inset-0 z-50 overflow-hidden bg-gray-900 bg-opacity-50"
                    onClick={(e) => {
                        // Close modal when clicking on backdrop (but not on modal content)
                        if (e.target === e.currentTarget) {
                            setSelectedPropertyId(null);
                        }
                    }}
                >
                    <div className="absolute inset-0 overflow-hidden">
                        <div className="absolute inset-y-0 right-0 flex max-w-full pl-10">
                            <div className="w-screen max-w-2xl">
                                <div className="h-full overflow-y-auto bg-white shadow-xl rounded-l-2xl">
                                    <div className="relative flex flex-col h-full">
                                        <PropertyDetailErrorBoundary onClose={() => setSelectedPropertyId(null)}>
                                            <PropertyDetail
                                                propertyId={selectedPropertyId}
                                                isDrawer={true}
                                                onClose={() => setSelectedPropertyId(null)}
                                            />
                                        </PropertyDetailErrorBoundary>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <PRSWizard isOpen={showWizard} onClose={() => setShowWizard(false)} />

            <Footer />
        </div>
    );
}
